import { useEffect, useMemo, useState } from "react";
import { twistNow } from "../../../../engine/bench";
import { funBoard } from "../../../../engine/ranking";
import type { EventType } from "../../../../engine/types";
import { BenchFeed, BenchNow, FunPoints } from "./BenchSection";
import { ConfirmButton, Stepper, Toggle, mmss, type TabProps } from "./common";
import { Fold } from "./Fold";

/** What each mini game is worth, and how often one comes by itself. Set before the day, so it lives in a fold. */
function TypesEditor({ t, state }: TabProps) {
  const saved = state.config.eventTypes;
  const [types, setTypes] = useState<EventType[]>(() => structuredClone(saved));
  const dirty = JSON.stringify(types) !== JSON.stringify(saved);
  // follow the server while nothing is being edited here
  useEffect(() => {
    if (!dirty) setTypes(structuredClone(saved));
  }, [saved]);

  const edit = (i: number, patch: Partial<EventType>) =>
    setTypes((all) => all.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  return (
    <>
      <Stepper label="Chance of a random mini game each round" value={Math.round(state.config.randomEventChance * 100)} step={5}
        format={(v) => `${v}%`}
        onChange={(v) => t.send({ type: "config.updated", patch: { randomEventChance: Math.min(100, Math.max(0, v)) / 100 } })} />
      <p className="ad-note">Fun points multiply each team's points; the bonus goes to the winners. "Counts on main" puts the main multiplier and bonus on the main board too.</p>
      <ul className="ad-list">
        {types.map((e, i) => (
          <li key={e.id} className="ad-etype">
            <div className="ad-etype-name">
              {e.name}
              <span className="ad-note"> {e.kind === "scoring" ? "a whole game" : "a twist"}, every court{e.target ? `, to ${e.target}` : ""}</span>
            </div>
            <div className="ad-stack">
              <Stepper label="Main board multiplier" value={e.mainMultiplier} step={0.5} onChange={(v) => edit(i, { mainMultiplier: v })} />
              <Stepper label="Fun board multiplier" value={e.funMultiplier} step={0.5} onChange={(v) => edit(i, { funMultiplier: v })} />
              <Stepper label="Winners' bonus" value={e.winBonus} onChange={(v) => edit(i, { winBonus: v })} />
            </div>
            <Toggle label="Counts on main" on={e.countsOnMain} onChange={(on) => edit(i, { countsOnMain: on })} />
          </li>
        ))}
      </ul>
      <div className="ad-grid2">
        <button type="button" className="btn" disabled={!dirty} onClick={() => t.send({ type: "config.updated", patch: { eventTypes: types } })}>
          Save event types
        </button>
        <button type="button" className="btn quiet" disabled={!dirty} onClick={() => setTypes(structuredClone(saved))}>
          Undo edits
        </button>
      </div>
    </>
  );
}

const IN_WORDS = ["two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

/** What the random switch does, from the chance as it is set: "about one round in four". Two lines at most on a 360 px phone. */
function randomLine(chance: number): string {
  const oneIn = chance > 0 ? Math.round(1 / chance) : 0;
  const share = oneIn >= 2 && oneIn <= 10 ? `one round in ${IN_WORDS[oneIn - 2]}` : `${Math.round(chance * 100)}% of rounds`;
  return `Random: about ${share}`;
}

/**
 * Mini games by hand: a scoring one for the whole of the next round, or a twist that starts now,
 * and the switch for the ones that come by themselves. Above them all, the switch for the day:
 * off ends a twist, takes the mini game off the next round, and hides the keys.
 */
function MiniGames({ t, state }: TabProps) {
  const types = state.config.eventTypes;
  const scoring = types.filter((e) => e.kind === "scoring");
  const twists = types.filter((e) => e.kind === "handicap");
  const nextNo = state.rounds.length + 1;
  const draft = state.draft;
  // the one put on the next round: it sits on every court of the preview
  const first = draft?.courts[0]?.eventId ?? null;
  const set = first !== null && draft?.courts.every((c) => c.eventId === first) ? first : null;
  const playingNo = state.clock.phase === "playing" ? state.clock.roundNo : null;
  const twist = twistNow(state, t.now());
  const seconds = Math.round(state.config.twistMs / 1000);
  const on = !state.miniGamesOff;
  const toggle = <Toggle label="Mini games" on={on} onChange={(v) => t.send({ type: "minigames.set", on: v })} />;

  if (!on) {
    return (
      <section className="ad-card">
        <h2>Mini games</h2>
        {toggle}
        <p className="ad-note">Mini games are off. Nothing comes on by itself.</p>
      </section>
    );
  }

  return (
    <section className="ad-card">
      <h2>Mini games</h2>
      {toggle}

      {twist && (
        <div className="ad-twist" role="status">
          <div className="ad-twist-what">
            <b>{twist.name}</b>
            <span>{twist.on ? "Running" : "Starts in"}</span>
          </div>
          <span className="ad-twist-count">{mmss(twist.leftMs)}</span>
          <button type="button" className="btn quiet ad-wide" onClick={() => t.send({ type: "twist.stopped" })}>
            Stop the twist
          </button>
        </div>
      )}

      <h3>Twists: {seconds} seconds, every court, {playingNo === null ? "once a round is on" : "starts now"}</h3>
      <div className="ad-grid2">
        {twists.map((e) => (
          <button key={e.id} type="button" className="btn ad-mini" disabled={playingNo === null}
            onClick={() => playingNo !== null && t.send({ type: "event.fired", roundNo: playingNo, eventId: e.id, courts: "all" })}>
            {e.name}
          </button>
        ))}
      </div>

      <h3>For the whole of round {nextNo}, every court</h3>
      <div className="ad-grid2">
        {scoring.map((e) => (
          <button key={e.id} type="button" className={"btn ad-mini" + (set === e.id ? " on" : "")} aria-pressed={set === e.id}
            disabled={!draft} onClick={() => t.send({ type: "event.fired", roundNo: nextNo, eventId: e.id, courts: "all" })}>
            <span>{e.name}</span>
            {set === e.id && <span className="ad-mini-state">Set for round {nextNo}</span>}
          </button>
        ))}
      </div>
      <p className="ad-note">
        {!draft
          ? "The next round has not been drawn yet. Once it is shown as up next, one of these can go on it."
          : set
            ? `Round ${nextNo} is played to it on every court.`
            : `Tap one to put it on round ${nextNo}.`}
      </p>

      <Toggle label={randomLine(state.config.randomEventChance)} on={state.config.randomEvents}
        onChange={(on) => t.send({ type: "config.updated", patch: { randomEvents: on } })} />
    </section>
  );
}

const COURT_WORDS: Record<number, string> = { 2: "two", 3: "three", 4: "four" };

/**
 * What is used on the day first: mini games, the Mexicano switch, the bench's mute and running
 * curses. Then, each in a fold: what the bench has said, the fun points, and the set-up.
 */
export function EventsTab(props: TabProps) {
  const { t, state } = props;
  const mexicano = state.mode === "mexicano";
  const heckles = state.bench.heckles.length;
  const withFun = useMemo(() => funBoard(state).filter((r) => r.funPoints > 0).length, [state]);

  return (
    <div className="ad-tab">
      <MiniGames {...props} />

      <section className="ad-card">
        <h2>Mode: {mexicano ? "Mexicano" : "Americano"}</h2>
        {/* the key first, so it is whole on the first screen of a 360 px phone; what the mode means under it */}
        {mexicano ? (
          <button type="button" className="btn ad-wide" onClick={() => t.send({ type: "mode.switched", mode: "americano" })}>
            Back to Americano
          </button>
        ) : (
          <ConfirmButton label="Switch to Mexicano" className="btn ad-wide" yes="Switch"
            question={`Switch during a game, not in the changeover. From then on the next courts appear once all ${COURT_WORDS[state.config.courts] ?? state.config.courts} scores are in.`}
            onConfirm={() => t.send({ type: "mode.switched", mode: "mexicano" })} />
        )}
        <p className="ad-note">{mexicano ? "Courts are drawn from the board, top four on court 1." : "Rotating partners, nobody partners the same person twice."}</p>
      </section>

      <BenchNow {...props} />

      <Fold id="events.feed" title="Bench feed" summary={`${heckles} ${heckles === 1 ? "heckle" : "heckles"}`}>
        <BenchFeed {...props} />
      </Fold>

      <Fold id="events.fun" title="Fun points" summary={`${withFun} with points`}>
        <FunPoints {...props} />
      </Fold>

      <Fold id="events.types" title="Event types" summary={`${state.config.eventTypes.length} mini games`}>
        <TypesEditor {...props} />
      </Fold>
    </div>
  );
}
